/**
 * ProposalTimeline - Timeline rica de histórico e eventos da proposta
 *
 * Padrão visual herdado da timeline do modal de Cobranças (badge circular,
 * conector vertical, destaque no evento mais recente), enriquecido com as
 * informações de engajamento do link público:
 *   - número ordinal da visualização (1ª, 2ª, 3ª...) por sessão distinta;
 *   - tempo ativo que o cliente permaneceu na página (metadata.duration_seconds);
 *   - origem do evento (Cliente / Usuário / Sistema).
 *
 * Toda a formatação (ordinais, duração, agrupamento por dia) é feita no cliente:
 * nenhum cálculo adicional é empurrado para o banco.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-12 v2.4.0 - Exibe o motivo/observação informado pelo usuário
 *            (cancelamento) ou pelo cliente (recusa na landing) em linha própria
 *            "Motivo: ...", lendo de history.notes/metadata.reason e do metadata
 *            dos eventos. Adicionados os eventos `cancelled` e `superseded` ao mapa.
 *   RELEASE: 2026-08-12 v2.2.0 - Exibe o usuário responsável pelos eventos internos
 *            (criação, PDF solicitado/gerado, envio) usando o novo campo `actor_name`
 *            retornado por get_proposal_details, com fallback para metadata.actor_name
 *            (nome informado pelo cliente no aceite/recusa)
 *   RELEASE: 2026-08-11 v2.1.0 - Nova variante 'compact' para a coluna lateral do
 *            modal de detalhes refatorado (badges menores, sem card em volta)
 *   RELEASE: 2026-08-11 v2.0.0 - Redesign completo (badges, conector, agrupamento

 *            por dia, nº da visualização e tempo na página)
 *   RELEASE: 2026-08-11 v1.0.0 - Criação (redesign do modal de detalhes da proposta)
 */

import React from 'react';
import {
  FilePlus2,
  FileText,
  Send,
  Eye,
  CheckCircle2,
  XCircle,
  Clock,
  Ban,
  Link2,
  Download,
  Loader2,
  History,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { PROPOSAL_STATUS_LABELS, type ProposalStatus } from '@/hooks/useDealProposals';

/** Registro de mudança de situação */
interface StatusHistoryEntry {
  new_status: string;
  changed_by_name?: string | null;
  created_at: string;
  /** Motivo derivado na RPC (metadata->>'reason') */
  notes?: string | null;
  /** Metadata bruta do histórico (contém `reason`, `actor_name`, etc.) */
  metadata?: Record<string, unknown> | null;
}


/** Evento de tracking (link público, PDF, etc.) */
interface ProposalEvent {
  event_type: string;
  session_id?: string | null;
  created_at: string;
  /** Nome do usuário do sistema responsável (JOIN em profiles feito na RPC) */
  actor_name?: string | null;
  metadata?: Record<string, unknown> | null;
}

interface ProposalTimelineProps {
  history: StatusHistoryEntry[];
  events: ProposalEvent[];
  /**
   * 'default': cards com borda (uso em largura total).
   * 'compact': versão enxuta para a coluna lateral do modal de detalhes.
   */
  variant?: 'default' | 'compact';
}


/** Origem do registro, exibida como badge discreto */
type EntrySource = 'client' | 'user' | 'system';

/** Item normalizado exibido na timeline */
interface TimelineEntry {
  key: string;
  icon: LucideIcon;
  iconClass: string;
  badgeClass: string;
  title: string;
  description?: string | null;
  /** Motivo/observação informado pelo usuário ou pelo cliente (auditoria) */
  reason?: string | null;

  source: EntrySource;
  createdAt: string;
}

/** Ícone + cores por situação da proposta */
const STATUS_ICONS: Record<string, { icon: LucideIcon; icon_class: string; badge: string }> = {
  draft: { icon: FilePlus2, icon_class: 'text-muted-foreground', badge: 'bg-muted' },
  generated: { icon: FileText, icon_class: 'text-blue-600 dark:text-blue-400', badge: 'bg-blue-500/10' },
  sent: { icon: Send, icon_class: 'text-indigo-600 dark:text-indigo-400', badge: 'bg-indigo-500/10' },
  viewed: { icon: Eye, icon_class: 'text-amber-600 dark:text-amber-400', badge: 'bg-amber-500/10' },
  accepted: { icon: CheckCircle2, icon_class: 'text-emerald-600 dark:text-emerald-400', badge: 'bg-emerald-500/10' },
  rejected: { icon: XCircle, icon_class: 'text-destructive', badge: 'bg-destructive/10' },
  expired: { icon: Clock, icon_class: 'text-orange-600 dark:text-orange-400', badge: 'bg-orange-500/10' },
  cancelled: { icon: Ban, icon_class: 'text-muted-foreground', badge: 'bg-muted' },
  superseded: { icon: History, icon_class: 'text-muted-foreground', badge: 'bg-muted' },
};

/** Ícone, cores, rótulo em pt-BR e origem por tipo de evento de tracking */
const EVENT_MAP: Record<
  string,
  { icon: LucideIcon; icon_class: string; badge: string; label: string; source: EntrySource }
> = {
  created: { icon: FilePlus2, icon_class: 'text-muted-foreground', badge: 'bg-muted', label: 'Proposta criada', source: 'user' },
  pdf_requested: { icon: Loader2, icon_class: 'text-blue-600 dark:text-blue-400', badge: 'bg-blue-500/10', label: 'PDF solicitado', source: 'user' },
  pdf_generated: { icon: FileText, icon_class: 'text-indigo-600 dark:text-indigo-400', badge: 'bg-indigo-500/10', label: 'PDF gerado', source: 'system' },
  pdf_failed: { icon: XCircle, icon_class: 'text-destructive', badge: 'bg-destructive/10', label: 'Falha ao gerar PDF', source: 'system' },
  pdf_downloaded: { icon: Download, icon_class: 'text-muted-foreground', badge: 'bg-muted', label: 'PDF baixado', source: 'user' },
  downloaded_pdf: { icon: Download, icon_class: 'text-sky-600 dark:text-sky-400', badge: 'bg-sky-500/10', label: 'PDF baixado pelo cliente', source: 'client' },
  link_generated: { icon: Link2, icon_class: 'text-indigo-600 dark:text-indigo-400', badge: 'bg-indigo-500/10', label: 'Link público gerado', source: 'user' },
  sent: { icon: Send, icon_class: 'text-indigo-600 dark:text-indigo-400', badge: 'bg-indigo-500/10', label: 'Proposta enviada', source: 'user' },
  opened: { icon: Eye, icon_class: 'text-amber-600 dark:text-amber-400', badge: 'bg-amber-500/10', label: 'Link aberto pelo cliente', source: 'client' },
  viewed: { icon: Eye, icon_class: 'text-amber-600 dark:text-amber-400', badge: 'bg-amber-500/10', label: 'Visualizada pelo cliente', source: 'client' },
  public_viewed: { icon: Eye, icon_class: 'text-amber-600 dark:text-amber-400', badge: 'bg-amber-500/10', label: 'Visualizada pelo cliente', source: 'client' },
  accepted: { icon: CheckCircle2, icon_class: 'text-emerald-600 dark:text-emerald-400', badge: 'bg-emerald-500/10', label: 'Proposta aceita', source: 'client' },
  rejected: { icon: XCircle, icon_class: 'text-destructive', badge: 'bg-destructive/10', label: 'Proposta recusada', source: 'client' },
  expired: { icon: Clock, icon_class: 'text-orange-600 dark:text-orange-400', badge: 'bg-orange-500/10', label: 'Proposta expirada', source: 'system' },
  // v2.4.0: eventos internos que apareciam com o rótulo cru (ex.: "cancelled")
  cancelled: { icon: Ban, icon_class: 'text-muted-foreground', badge: 'bg-muted', label: 'Proposta cancelada', source: 'user' },
  superseded: { icon: History, icon_class: 'text-muted-foreground', badge: 'bg-muted', label: 'Proposta substituída', source: 'system' },
};

/**
 * Extrai o motivo/observação registrado, priorizando `notes` (derivado na RPC)
 * e caindo para `metadata.reason` (eventos e registros antigos).
 * Retorna null quando não há texto útil.
 */
function extractReason(
  notes?: string | null,
  metadata?: Record<string, unknown> | null,
): string | null {
  const raw = (notes ?? (metadata as any)?.reason) as unknown;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed ? trimmed : null;
}


/** Rótulos das origens */
const SOURCE_LABELS: Record<EntrySource, string> = {
  client: 'Cliente',
  user: 'Usuário',
  system: 'Sistema',
};

/** Formata apenas o horário (data fica no separador do dia) */
function formatTime(value: string): string {
  return new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

/** Formata o rótulo do dia (Hoje / Ontem / dd de mês) */
function formatDayLabel(value: string): string {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86400000);
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (sameDay(date, today)) return 'Hoje';
  if (sameDay(date, yesterday)) return 'Ontem';
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
}

/** Converte segundos em rótulo curto legível (18s, 2min 05s, 1h 04min) */
function formatDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(totalSeconds));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (minutes < 60) return `${minutes}min${rest ? ` ${String(rest).padStart(2, '0')}s` : ''}`;
  const hours = Math.floor(minutes / 60);
  const restMin = minutes % 60;
  return `${hours}h${restMin ? ` ${String(restMin).padStart(2, '0')}min` : ''}`;
}

/** Ordinal feminino em pt-BR (1ª, 2ª, ...) */
function ordinal(index: number): string {
  return `${index}ª`;
}

/**
 * Timeline vertical unificada de situações e eventos da proposta.
 */
export function ProposalTimeline({ history, events, variant = 'default' }: ProposalTimelineProps) {
  // Modo enxuto usado na coluna lateral (badges menores, sem card em volta)
  const isCompact = variant === 'compact';

  const entries = React.useMemo<TimelineEntry[]>(() => {
    // Ordinal de visualização por sessão distinta (calculado do mais antigo p/ o mais novo)
    const viewOrdinalByKey = new Map<string, number>();
    const seenSessions = new Map<string, number>();
    let viewCounter = 0;

    const viewEvents = (events || [])
      .filter((e) => ['viewed', 'opened', 'public_viewed'].includes(e.event_type))
      .slice()
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

    viewEvents.forEach((e) => {
      const sessionKey = e.session_id || `anon-${e.created_at}`;
      let ordinalValue = seenSessions.get(sessionKey);
      if (ordinalValue === undefined) {
        viewCounter += 1;
        ordinalValue = viewCounter;
        seenSessions.set(sessionKey, ordinalValue);
      }
      viewOrdinalByKey.set(`${e.event_type}-${e.created_at}`, ordinalValue);
    });

    const fromHistory: TimelineEntry[] = (history || []).map((h, idx) => {
      const meta = STATUS_ICONS[h.new_status] ?? {
        icon: History,
        icon_class: 'text-muted-foreground',
        badge: 'bg-muted',
      };
      // v2.4.0: motivo/observação registrado pelo usuário ou pelo cliente.
      // Vem em `notes` (derivado na RPC) ou direto em `metadata.reason`.
      const reason = extractReason(h.notes, h.metadata);
      return {
        key: `h-${idx}-${h.created_at}`,
        icon: meta.icon,
        iconClass: meta.icon_class,
        badgeClass: meta.badge,
        title: `Situação: ${PROPOSAL_STATUS_LABELS[h.new_status as ProposalStatus] ?? h.new_status}`,
        description: h.changed_by_name ? `por ${h.changed_by_name}` : null,
        reason,
        source: h.changed_by_name ? 'user' : 'system',
        createdAt: h.created_at,
      };
    });

    const fromEvents: TimelineEntry[] = (events || []).map((e, idx) => {
      const meta = EVENT_MAP[e.event_type];
      const details: string[] = [];

      const ordinalValue = viewOrdinalByKey.get(`${e.event_type}-${e.created_at}`);
      if (ordinalValue) details.push(`${ordinal(ordinalValue)} visualização`);

      const duration = Number((e.metadata as any)?.duration_seconds ?? 0);
      if (duration > 0) details.push(`${formatDuration(duration)} na página`);

      // Responsável: usuário do sistema (actor_name da RPC) ou nome informado
      // pelo cliente no aceite/recusa (metadata.actor_name)
      const actor = e.actor_name ?? (e.metadata as any)?.actor_name;
      if (typeof actor === 'string' && actor.trim()) details.push(`por ${actor.trim()}`);

      return {
        key: `e-${idx}-${e.created_at}`,
        icon: meta?.icon ?? History,
        iconClass: meta?.icon_class ?? 'text-muted-foreground',
        badgeClass: meta?.badge ?? 'bg-muted',
        title: meta?.label ?? e.event_type,
        description: details.length ? details.join(' · ') : null,
        // v2.4.0: motivo do cancelamento/recusa gravado no evento
        reason: extractReason(null, e.metadata),
        source: meta?.source ?? 'system',
        createdAt: e.created_at,
      };
    });


    // v2.3.0 — Defesa contra duplicidade histórica: se dois registros de situação
    // idênticos caírem no mesmo minuto, mantém apenas o mais descritivo.
    const dedupedHistory: TimelineEntry[] = [];
    const seenHistoryKeys = new Map<string, number>();
    fromHistory.forEach((entry) => {
      const minuteKey = `${entry.title}|${entry.createdAt.slice(0, 16)}`;
      const existingIdx = seenHistoryKeys.get(minuteKey);
      if (existingIdx === undefined) {
        seenHistoryKeys.set(minuteKey, dedupedHistory.length);
        dedupedHistory.push(entry);
        return;
      }
      // Mantém a versão mais informativa (com motivo ou com "por Fulano")
      const current = dedupedHistory[existingIdx];
      const score = (item: TimelineEntry) => (item.reason ? 2 : 0) + (item.description ? 1 : 0);
      if (score(entry) > score(current)) {
        dedupedHistory[existingIdx] = entry;
      }

    });

    return [...dedupedHistory, ...fromEvents].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [history, events]);


  if (entries.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border py-8 text-center">
        <History className="h-5 w-5 mx-auto text-muted-foreground mb-2" />
        <p className="text-xs text-muted-foreground">Sem movimentações registradas.</p>
      </div>
    );
  }

  // Agrupa por dia preservando a ordem (mais recente primeiro)
  const groups: Array<{ label: string; items: TimelineEntry[] }> = [];
  entries.forEach((entry) => {
    const label = formatDayLabel(entry.createdAt);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(entry);
    else groups.push({ label, items: [entry] });
  });

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div key={group.label}>
          {/* Separador de dia */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {group.label}
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="relative">
            {/* Conector vertical alinhado ao centro dos badges */}
            <div
              className={cn(
                'absolute top-3 bottom-3 w-px bg-border',
                isCompact ? 'left-[11px]' : 'left-[15px]',
              )}
              aria-hidden
            />

            <div className={cn(isCompact ? 'space-y-3' : 'space-y-2.5')}>
              {group.items.map((entry, index) => {
                const Icon = entry.icon;
                const isLatest = groups[0] === group && index === 0;

                return (
                  <div key={entry.key} className={cn('relative flex', isCompact ? 'gap-2.5' : 'gap-3')}>
                    {/* Badge circular do ícone */}
                    <div
                      className={cn(
                        'relative z-[1] shrink-0 rounded-full flex items-center justify-center',
                        isCompact ? 'h-6 w-6' : 'h-8 w-8',
                        entry.badgeClass,
                        isLatest && 'ring-2 ring-primary/30 ring-offset-2 ring-offset-background',
                      )}
                    >
                      <Icon className={cn(isCompact ? 'h-3 w-3' : 'h-4 w-4', entry.iconClass)} />
                      {/* Marcador de novidade no evento mais recente */}
                      {isLatest && (
                        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-primary" />
                      )}
                    </div>

                    {/* Conteúdo do evento */}
                    <div
                      className={cn(
                        'flex-1 min-w-0',
                        !isCompact && 'rounded-lg border border-border/60 bg-card px-3 py-2',
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p
                          className={cn(
                            'font-medium text-foreground min-w-0 break-words',
                            isCompact ? 'text-[11px]' : 'text-xs',
                          )}
                        >
                          {entry.title}
                        </p>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
                          {formatTime(entry.createdAt)}
                        </span>
                      </div>

                      {entry.description && (
                        <p
                          className={cn(
                            'text-muted-foreground mt-0.5 break-words',
                            isCompact ? 'text-[10px]' : 'text-[11px]',
                          )}
                        >
                          {entry.description}
                        </p>
                      )}

                      {/* v2.4.0: motivo/observação registrado (auditoria) */}
                      {entry.reason && (
                        <p
                          className={cn(
                            'mt-1 break-words text-foreground/90 line-clamp-3',
                            isCompact ? 'text-[10px]' : 'text-[11px]',
                          )}
                          title={entry.reason}
                        >
                          <span className="font-medium text-muted-foreground">Motivo: </span>
                          {entry.reason}
                        </p>
                      )}


                      {!isCompact && (
                        <span className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" />
                          {SOURCE_LABELS[entry.source]}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      ))}
    </div>
  );
}
